import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Fab,
    Fade,
    Skeleton,
    useTheme
} from "@mui/material";
import ItemViewChips from "../components/ItemViewChips";
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import Api from "../services/Api";
import { processUrl } from "../services/EbayUrl";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import { Carousel } from 'react-responsive-carousel';
import NoImageView from "../components/NoImageView";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import OpenInBrowserIcon from "@mui/icons-material/OpenInBrowser";
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined';
import PhotoIcon from '@mui/icons-material/Photo';
import Scroll from "react-scroll";
import LinkOutlinedIcon from '@mui/icons-material/CopyAllOutlined';
import useMediaQuery from "@mui/material/useMediaQuery";
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import { MobileTooltip } from "../components/Tooltip";
import DOMPurify from "dompurify"
import CheckIcon from "@mui/icons-material/Check";

let controller = new AbortController();

let shouldClose = false;

function Image({ url, onError }) {
    return (
        <img className="listing-transition-image" key={url} src={url}
            loading="lazy"
            decoding="async"
            style={{
                objectFit: "contain",
                height: 'auto',
                width: 'auto',
                maxWidth: '100%',
                maxHeight: '100%',
                margin: 'auto',
                borderRadius: "12px",
            }}
            role="presentation"
            alt=""
            onError={onError}
        />
    )
}
const ItemView = React.memo(ItemViewBase)
function ItemViewBase({
    sellersDisabled,
    countriesDisabled,
    auth,
    preload,
    updateUser,
    open,
    handleClose,
    item,
    imageSelection,
    onImageChange,
    preferredEbay,
    sort,
    setRecentItems,
    isUser,
    isRecent, blockedCountries, blockedSellers
}) {
    // Derive the high-res image list without mutating the shared `item` prop on
    // every render. The previous assignment re-ran this map each render and
    // leaked the transformed URLs back onto the grid's item objects.
    const additionalImages = useMemo(
        () => item.additionalImages?.map((image) => ({ imageUrl: image.imageUrl?.slice(0, image.imageUrl?.lastIndexOf('/')) + '/s-l960.jpg' })),
        [item]
    );
    const theme = useTheme();
    let isDarkMode = theme.palette.mode === "dark";
    let scroller = Scroll.animateScroll;
    let Element = Scroll.Element;
    let itemTitle = item.title;
    let itemUrl = preferredEbay ? processUrl(preferredEbay, item.itemAffiliateWebUrl) : item.itemAffiliateWebUrl;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const isPreload = preload?.id === item?.legacyItemId;
    const imagesObject = data?.additionalImages ?? additionalImages ?? (isPreload && preload?.images) ?? [];
    const [images, setImages] = useState(null);
    const firstImage = { imageUrl: item.thumbnailImages?.[0]?.imageUrl?.slice(0, item.thumbnailImages?.[0]?.imageUrl?.lastIndexOf('/')) + '/s-l960.jpg' ?? item.image?.imageUrl?.slice(0, item.image?.imageUrl?.lastIndexOf('/')) + '/s-l960.jpg' ?? '' }
    const dirtyDescription = data?.description ?? (isPreload && preload?.description) ?? '';
    const description = useMemo(() => DOMPurify.sanitize(dirtyDescription, { FORBID_TAGS: ['style', 'script', 'link', 'img', 'input', 'table', 'ul', 'ol', 'li', 'button', 'a'] }), [dirtyDescription])
    const [copied, setCopied] = useState(false);
    const [copyTip, setCopyTip] = useState(false);
    const [selectedIndex, setSelectedIndex] = useState(imageSelection?.index ?? 0);
    const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

    function updateFromPreload() {
        if (preload && preload.id === item.legacyItemId && (preload.images || preload.description)) {
            controller.abort();
            return true;
        } else {
            return false;
        }
    }

    useLayoutEffect(() => {
        if (!open) {
            setTimeout(() => {
                setData(null);
                setImages(null);
                setCopyTip(false);
                setIsScroll(false);
                setSelectedIndex(0);
                shouldClose = false;
            }, 125);
            return;
        }
        if (images) return;
        if (imagesObject?.length > 0) setImages(imagesObject);
    }, [imagesObject, images, open]);

    useLayoutEffect(() => {
        if (open) setSelectedIndex(imageSelection?.index ?? 0);
    }, [imageSelection?.index, item.itemId, open]);

    useLayoutEffect(() => {
        if (loading) {
            if (updateFromPreload()) setLoading(false);
        }
    }, [preload, loading])

    async function handleCopy() {
        try {
            await navigator.clipboard.writeText(itemUrl);
        } catch {

        }
        setCopyTip(true)
        setCopied(true);
        setTimeout(() => { setCopyTip(false); }, 2000);
        setTimeout(() => { setCopied(false); }, 2500);

    }

    useLayoutEffect(() => {
        if (!open) return;
        if (!updateFromPreload()) {
            async function getItem(id) {
                setLoading(true);
                controller.abort();
                controller = new AbortController();
                let data;
                try {
                    data = await Api.fetchItem(auth, id, controller);
                } catch (e) {
                    return;
                }
                setData(data);
                setLoading(false)
            }
            getItem(item.legacyItemId);
        }
    }, [open, item, preload]);

    useEffect(() => {
        if (open) {
            if (!isRecent && item?.itemId) {
                React.startTransition(() => {
                    setRecentItems(prev => [item.itemId, ...prev])
                });
                Api.updateRecents(auth, item.itemId)
            }
        }
    }, [open, isRecent]);

    const getImagesObject = function (images) {
        const result = new Array(images?.length);
        let url;
        for (let i = 0; i < images?.length; i++) {
            url = images?.[i]?.imageUrl;
            result[i] = getImageObject(url);
        }
        return result;
    }
    const [scale, setScale] = useState(true);

    function getImageObject(url) {
        return (
            <TransformWrapper pan={{ lockAxisX: scale, lockAxisY: scale }}
                doubleClick={{
                    step: 30, mode: scale ? "zoomIn" : "reset",
                }}
                onPanning={(e) => setScale(e.scale === 1)}
                onPanningStart={(e) => setScale(e.scale === 1)}
                onPinching={(e) => setScale(e.scale === 1)} onWheel={(e) => setScale(e.scale === 1)}
                key={'wrapper' + url}>
                <TransformComponent key={'component' + url}>
                    <div style={{
                        paddingLeft: 10,
                        paddingRight: 10,
                        paddingBottom: 20,
                        display: "flex",
                        height: '100%',
                        width: '100%'
                    }}>
                        <Image url={url} onError={() => setImages([])} />
                    </div>
                </TransformComponent>
            </TransformWrapper>)
    }

    const [isScroll, setIsScroll] = useState(false);
    const ref = useRef(null);
    const handleScroll = (e) => {
        if (!ref.current) return;
        if (isDesktop) return;
        const scrollTop = e.target.scrollTop;
        setIsScroll(scrollTop > 75);
        ref.current.style.top = `-${Math.floor(scrollTop / 2)}px`;
        ref.current.style.opacity = `${(100 - (Math.floor(scrollTop / 7)))}%`;
    }
    const isScrolled = isScroll;
    const handlePhotoDismiss = () => {
        const ref = document.getElementById("item-view");
        if (ref.scrollTop === 0) return;
        scroller.scrollTo(-1, { containerId: "item-view", duration: 500 })
    }
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!isDesktop) return;
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                handleClose()
            } else if (event.key === 'Enter') {
                window.open(itemUrl, "_blank");
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        }
    }, [handleClose, isDesktop, itemUrl])
    if (!open) return null;

    return (<>
        <Dialog
            onMouseDown={(e) => {
                if (e.target?.classList?.contains?.("MuiPaper-root") || e.target?.classList?.contains?.("MuiDialog-container")) {
                    shouldClose = true;
                }
            }}
            onMouseUp={(e) => {
                if (shouldClose && ((e.target?.classList?.contains?.("MuiPaper-root") || e.target?.classList?.contains?.("MuiDialog-container")))) {
                    shouldClose = false;
                    handleClose();
                }
            }}
            PaperProps={{
                className: "hideScroll",
                id: "item-view",
                onScroll: handleScroll,
                style: isDesktop ? {
                    display: "flex",
                    flexDirection: "row",
                    width: "calc(100vw - 64px)",
                    maxWidth: "calc(100vw - 64px)",
                    height: "calc(100vh - 112px)",
                    alignItems: "center",
                    gap: 0
                } : {}
            }} key={"main"} className={'ItemView ItemViewPaper'}
            open={true}
            onClose={handleClose}
            transitionDuration={0}
            fullWidth={true}>
            <header className={"sticky"} ref={ref} style={{
                minHeight: "calc(100% - 140px)",
                width: "100%",
                position: "sticky",
                ...(isDesktop ? { height: "100%" } : {})
            }} onClick={handlePhotoDismiss}>
                {images?.length > 0 || firstImage.imageUrl !== '' ?
                    <Carousel
                        renderArrowNext={isDesktop ? (clickHandler, hasNext) =>
                            <Box className="ListingCarouselNext" onClick={clickHandler} sx={{
                                pointerEvents: hasNext ? "" : "none",
                                cursor: hasNext ? "pointer" : undefined,
                                position: "absolute",
                                top: 0,
                                right: 0,
                                height: "100%",
                                display: "flex",
                                width: "15%",
                                minWidth: "fit-content",
                                alignItems: "center",
                                justifyContent: "flex-end",
                                opacity: 0.5,
                                transition: "opacity 195ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                                ":hover": { opacity: 1 }
                            }}>
                                <Fade in={hasNext}>
                                    <div>
                                        <Fab size={"small"} sx={{ margin: "16px" }} color={"primary"} aria-label="next">
                                            <NavigateNextIcon fontSize={"large"} />
                                        </Fab>
                                    </div>
                                </Fade>
                            </Box> : undefined
                        }
                        renderArrowPrev={isDesktop ? (clickHandler, hasNext) =>
                            <Box className="ListingCarouselPrevious" onClick={clickHandler} sx={{
                                pointerEvents: hasNext ? "" : "none",
                                cursor: hasNext ? "pointer" : undefined,
                                position: "absolute",
                                top: 0,
                                left: 0,
                                zIndex: 2,
                                height: "100%",
                                display: "flex",
                                width: "15%",
                                minWidth: "fit-content",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                opacity: 0.5,
                                transition: "opacity 195ms cubic-bezier(0.4, 0, 0.2, 1) 0ms",
                                ":hover": { opacity: 1 }
                            }}>
                                <Fade in={hasNext}>
                                    <div>
                                        <Fab size={"small"} sx={{ margin: "16px" }} color={"primary"} aria-label="back">
                                            <NavigateBeforeIcon fontSize={"large"} />
                                        </Fab>
                                    </div>
                                </Fade>
                            </Box> : undefined
                        }
                        renderIndicator={(
                            onClickHandler,
                            isSelected,
                            index,
                            label) => {
                            const shouldShow = isSelected || index <= selectedIndex + 4 && index >= selectedIndex - 4;
                            return (
                                <Fade in={images?.length > 0}>
                                    <li
                                        className={isSelected ? "dot selected-dot" : images?.length >= 5 && (index === selectedIndex + 4
                                            || index === selectedIndex - 4) ? "dot extra-dot" :
                                            images?.length >= 5 && (index === selectedIndex + 3
                                                || index === selectedIndex - 3) ? "dot extra-dot-dot" :
                                                images?.length >= 5 && (index === selectedIndex + 2
                                                    || index === selectedIndex - 2) ? "dot extra-dot-dot-dot"
                                                    :
                                                    images?.length >= 5 && (index === selectedIndex + 1
                                                        || index === selectedIndex - 1) ? "dot extra-dot-dot-dot-dot"
                                                        : "dot"}
                                        onClick={onClickHandler}
                                        onKeyDown={onClickHandler}
                                        value={index}
                                        key={index}
                                        role="button"
                                        tabIndex={0}
                                        aria-label={`${label} ${index + 1}`}
                                        style={{
                                            width: !shouldShow ? 0 : undefined,
                                            margin: !shouldShow ? 0 : undefined,
                                            height: !shouldShow ? 0 : undefined
                                        }}
                                    />
                                </Fade>
                            );

                        }}
                        onChange={(index) => {
                            setSelectedIndex(index);
                            const hasFirstImage = firstImage.imageUrl !== '';
                            const imageUrl = hasFirstImage && index === 0
                                ? firstImage.imageUrl
                                : images?.[index - (hasFirstImage ? 1 : 0)]?.imageUrl;
                            onImageChange(item.itemId, imageUrl, index);
                            setScale(true);
                        }} selectedItem={selectedIndex} autoFocus sx={{ backgroundColor: 'transparent' }} showThumbs={false} useKeyboardArrows
                        infiniteLoop={true} showStatus={false} showArrows={images?.length > 0}
                        showIndicators={images?.length > 0} swipeable={true}
                        swipeScrollTolerance={scale && images?.length > 0 ? 5 : 10000}
                        preventMovementUntilSwipeScrollTolerance>
                        {firstImage.imageUrl !== '' ? getImageObject(firstImage.imageUrl ?? preload?.image?.imageUrl) : []}
                        {images?.length > 0 && getImagesObject(images)}
                    </Carousel> :
                    <>
                        <NoImageView view itemTitle={itemTitle} />
                    </>}
            </header>
            <div style={{
                minWidth: 0,
                overflow: isDesktop ? "hidden" : "",
                width: isDesktop ? "50%" : "",
                height: isDesktop ? "100%" : "",
                maxHeight: isDesktop ? "640px" : ""
            }}>
                <div style={{
                    position: "relative", ...(isDesktop ? {
                        height: "100%",
                        minHeight: "calc(100% - 12px)",
                        maxHeight: "calc(100% - 12px)",
                        display: "flex",
                        flexDirection: "column"
                    } : {})
                }}>
                    <div className={"sticky " + isDarkMode ? "dark-background" : ""} style={{
                        zIndex: 1,
                        position: "sticky",
                        top: '-0.5px',
                        backgroundColor: theme.palette.background.paper,
                        borderRadius: "12px 12px 0 0"
                    }}>
                        <DialogContent className="hideScroll"
                            sx={{ padding: '0.5em', paddingBottom: '0', minHeight: '42px' }}>
                            <div style={{ display: "flex" }}>
                                <ItemViewChips blockedCountries={blockedCountries} blockedSellers={blockedSellers}
                                    sellersDisabled={sellersDisabled} countriesDisabled={countriesDisabled}
                                    updateUser={updateUser} item={item} sort={sort} isUser={isUser} />
                                <div style={{ width: "3px", flexShrink: 0 }} />
                            </div>
                        </DialogContent>
                        <Element name={"description"}>
                            <DialogTitle
                                sx={{
                                    paddingBottom: '0',
                                    fontSize: '1em',
                                    overflow: 'hidden',
                                    minHeight: '3em',
                                    ...(isDesktop ? {} : {})
                                }}>{itemTitle}</DialogTitle>
                        </Element>
                        <Divider sx={{ paddingTop: 3, paddingBottom: 0, marginBottom: 0 }} />
                    </div>
                    <DialogContent
                        name={"description-start"}
                        sx={{
                            marginBottom: "-1px",
                            paddingBottom: '0',
                            color: isDarkMode ? "white" : "black",
                            zIndex: 0,
                            backgroundColor: theme.palette.background.paper,
                            overflowX: "hidden",
                            ...(isDesktop ? { flex: 1, display: "flex", marginBottom: "39.5px" } : {})
                        }} className={`${isDarkMode ? "dark-background" : ""}`}>
                        <Box sx={{
                            "*": {
                                color: isDarkMode ? "white!important" : "rgba(0, 0, 0, 0.87)!important",
                                backgroundColor: "transparent!important",
                                fontFamily: `"Roboto","Helvetica","Arial",sans-serif!important`,
                                fontSize: "1em!important",
                                width: "100%!important",
                                fontWeight: "normal!important",
                                textAlign: "left!important",
                                textDecoration: "none!important",
                                lineHeight: "1.6!important",
                                letterSpacing: "0.0075em!important",
                                fontStyle: "normal!important",
                                backgroundImage: "none!important"
                            }, minWidth: 0
                        }} dangerouslySetInnerHTML={{ __html: description }} />
                        {isDesktop && loading &&
                            <div style={{ flex: 1 }}>
                                <Skeleton />
                                <Skeleton />
                                <Skeleton />
                                <Skeleton />
                                <Skeleton width="60%" />
                            </div>}
                    </DialogContent>

                </div>
                <DialogActions className={"sticky " + isDarkMode ? "dark-background" : ""} sx={{
                    position: "sticky",
                    zIndex: 1,
                    width: '100%',
                    bottom: 0,
                    right: 0,
                    backgroundColor: theme.palette.background.paper,
                    ...(isDesktop ? { borderRadius: "0 0 12px 12px" } : {})
                }}>
                    {!isDesktop &&
                        <MobileTooltip enterDelay={500} title={isScrolled ? "Hide description" : "Show description"}>
                            <Button
                                onClick={() => {
                                    if (isScrolled) {
                                        scroller.scrollTo(-1, { containerId: "item-view", duration: 500 })
                                    } else {
                                        Scroll.scroller.scrollTo('description', {
                                            duration: 500,
                                            containerId: 'item-view',
                                            smooth: true,
                                            offset: -40
                                        })
                                    }
                                }}>
                                {!isScrolled ? <DescriptionIcon /> : <PhotoIcon />}
                            </Button>
                        </MobileTooltip>
                    }
                    <a style={{ textDecoration: "none" }} href={itemUrl} target="_blank" rel="noreferrer noopener">
                        <MobileTooltip enterDelay={500} title={"Open in eBay (enter)"}>
                            <Button onClick={() => {
                            }}><OpenInBrowserIcon /></Button>
                        </MobileTooltip>
                    </a>
                    <MobileTooltip
                        onClose={() => setCopyTip(false)}
                        onOpen={() => setCopyTip(true)}
                        open={copyTip}
                        override={copied}
                        enterDelay={500}
                        leaveDelay={copied ? 3000 : null}
                        title={copied ? "Copied to clipboard" : "Copy eBay link"}
                    >
                        <Button disabled={copied} onClick={handleCopy}>{!copied ? <LinkOutlinedIcon /> : <CheckIcon />}</Button>
                    </MobileTooltip>
                </DialogActions>
            </div>
        </Dialog>
    </>)
}

export default ItemView;


